import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import { Badge } from '@/components/ui/badge';

// Component to render the variable as a colored pill
const VariableComponent = ({ node }: any) => {
  const variable = node.attrs.variable;
  
  // Determine category and color based on variable prefix
  const getCategoryStyle = (variable: string) => {
    if (variable.includes('customer.') || variable.includes('contact.') || variable.includes('customer_') || variable.includes('contact_')) {
      return { variant: 'accent' as const, accent: 'blue-violet' as const };
    } else if (variable.includes('transaction.') || variable.includes('account.') || variable.includes('transaction_') || variable.includes('account_') || variable.includes('reference_') || variable.includes('due_date') || variable.includes('gross') || variable.includes('amount_') || variable.includes('days_') || variable.includes('last_payment') || variable.includes('case_') || variable.includes('outstanding_') || variable.includes('overdue_')) {
      return { variant: 'accent' as const, accent: 'teal' as const };
    } else if (variable.includes('company.') || variable.includes('credit_controller') || variable.includes('company_') || variable.includes('support_')) {
      return { variant: 'accent' as const, accent: 'purple' as const };
    } else if (variable.includes('payment.') || variable.includes('payment_')) {
      return { variant: 'accent' as const, accent: 'orange' as const };
    }
    return { variant: 'secondary' as const, accent: undefined };
  };

  const style = getCategoryStyle(variable);

  return (
    <NodeViewWrapper as="span" className="inline-flex items-center" data-variable={variable}>
      <Badge 
        variant={style.variant}
        accent={style.accent}
        className="text-[10px] font-mono cursor-default select-none mx-0.5"
        contentEditable={false}
        draggable={false}
      >
        {variable}
      </Badge>
    </NodeViewWrapper>
  );
};

// TipTap extension for variables
export const VariableExtension = Node.create({
  name: 'variable',

  group: 'inline',

  inline: true,

  atom: true,
  
  // Mark as selectable but not editable
  selectable: true,
  
  draggable: false,

  addAttributes() {
    return {
      variable: {
        default: null,
        parseHTML: element => element.getAttribute('data-variable'),
        renderHTML: attributes => {
          if (!attributes.variable) {
            return {};
          }
          return {
            'data-variable': attributes.variable,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-variable]',
        getAttrs: (element: HTMLElement | string) => {
          if (typeof element === 'string') return false;
          const variable = element.getAttribute('data-variable');
          return variable ? { variable } : false;
        },
      },
    ];
  },

  renderHTML({ node }: any) {
    // Return span with data attribute and the variable text as content
    // This ensures the variable is visible in HTML output (for saving/preview)
    return ['span', { 
      class: 'variable-node',
      'data-variable': node.attrs.variable
    }, node.attrs.variable];
  },

  addNodeView() {
    return ReactNodeViewRenderer(VariableComponent, {
      // Ensure the node view is always used
      stopEvent: () => true,
    });
  },

  addCommands() {
    return {
      insertVariable: (variable: string) => ({ commands }: { commands: { insertContent: (content: { type: string; attrs: Record<string, string> }) => boolean } }) => {
        return commands.insertContent({
          type: this.name,
          attrs: { variable },
        });
      },
    } as unknown as Partial<import('@tiptap/core').RawCommands>;
  },
});
